import { useMemo } from "react";
import { validator } from "@/utils/validator";
import {
  MAX_TEXT_INPUT_VALIDATION,
  MAX_INTEGER,
  JLPT_CERTIFICATE_REQUIRED_MESSAGE,
  SPECIFIC_SKILL_CERTIFICATE_REQUIRED_MESSAGE,
} from "@/constants/common";
import { EAttachmentDocumentType, ECaseMatchStatus, RESIDENCE_CARD_DOCUMENT_TYPE_IDS, TALENT_LOCATION } from "@/types/enum";
import { createMinDateTest, createMaxDateTest } from "@/features/case/utils/caseMatchHelper";

const TERMINAL_STATUSES = [
  ECaseMatchStatus.FAILED,
  ECaseMatchStatus.CANDIDATE_WITHDRAWN,
  ECaseMatchStatus.OFFER_CANCELLED,
  ECaseMatchStatus.SELECTION_CANCELLED,
];

// Document type group requirements for case-match certificate files.
// By default a group is satisfied when at least one of `acceptableTypeIds` is covered (any-of).
// `requireAll: true` makes the group satisfied only when ALL of `acceptableTypeIds` are covered.
// Mirrors the original per-field requirement logic:
// - 日本語合格証 + 特定技能合格証等 required for any non-BEFORE_PROPOSAL status
//   (特定技能合格証等 is skipped when the job's residence status includes 技人国).
// - 在留カード（表・裏）required when talent is LOCAL — both FRONT and BACK must be tagged
//   somewhere on the certificate files before the error clears.
type RequiredDocGroup = { label: string; acceptableTypeIds: number[]; requireAll?: boolean };

const buildRequiredCertificateGroups = (talentLocation?: number, exemptSpecifiedSkillCert?: boolean): RequiredDocGroup[] => {
  const groups: RequiredDocGroup[] = [{ label: "日本語合格証", acceptableTypeIds: [EAttachmentDocumentType.JAPANESE_LANGUAGE_CERTIFICATE] }];
  if (!exemptSpecifiedSkillCert) {
    groups.push({ label: "特定技能合格証等", acceptableTypeIds: [EAttachmentDocumentType.JAPANESE_SKILL_CERTIFICATE] });
  }
  if (talentLocation === TALENT_LOCATION.LOCAL) {
    groups.push({ label: "在留カード（表・裏）", acceptableTypeIds: [...RESIDENCE_CARD_DOCUMENT_TYPE_IDS], requireAll: true });
  }
  return groups;
};

const isGroupSatisfied = (group: RequiredDocGroup, covered: Set<number>): boolean => {
  return group.requireAll ? group.acceptableTypeIds.every((id) => covered.has(id)) : group.acceptableTypeIds.some((id) => covered.has(id));
};

const collectCoveredTypeIds = (fileTypes: (string | null | undefined)[]): Set<number> => {
  const covered = new Set<number>();
  fileTypes.forEach((csv) => {
    if (!csv) return;
    String(csv)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => {
        const n = Number(s);
        if (!Number.isNaN(n)) covered.add(n);
      });
  });
  return covered;
};

type TUseCaseMatchInfoSchemaOptions = {
  status?: number;
  requiresMajorAndJobType?: boolean;
  talentLocation?: number;
  exemptSpecifiedSkillCert?: boolean;
};

export const useCaseMatchInfoSchema = (options: TUseCaseMatchInfoSchemaOptions | number = {}) => {
  const {
    status,
    requiresMajorAndJobType = false,
    talentLocation,
    exemptSpecifiedSkillCert = false,
  } = typeof options === "number" ? { status: options } : options;

  const caseMatchInfoSchema = useMemo(() => {
    const isTerminalStatus = status !== undefined && status !== null && TERMINAL_STATUSES.includes(status as ECaseMatchStatus);

    const isRequiredForStatus = (fieldStatus: ECaseMatchStatus | ECaseMatchStatus[]) => {
      if (status === undefined || status === null) return false;
      if (isTerminalStatus) return false;
      const statusArray = Array.isArray(fieldStatus) ? fieldStatus : [fieldStatus];
      return statusArray.includes(status as ECaseMatchStatus);
    };

    const requiresProposalDate = isRequiredForStatus([
      ECaseMatchStatus.PROPOSING,
      ECaseMatchStatus.UNDER_SELECTION,
      ECaseMatchStatus.OFFER,
      ECaseMatchStatus.OFFER_ACCEPTED,
      ECaseMatchStatus.ALREADY_HIRED,
    ]);

    const requiresInterviewDate = isRequiredForStatus([
      ECaseMatchStatus.UNDER_SELECTION,
      ECaseMatchStatus.OFFER,
      ECaseMatchStatus.OFFER_ACCEPTED,
      ECaseMatchStatus.ALREADY_HIRED,
    ]);

    const requiresJapaneseLanguageProficiency = isRequiredForStatus([
      ECaseMatchStatus.PROPOSING,
      ECaseMatchStatus.UNDER_SELECTION,
      ECaseMatchStatus.OFFER,
      ECaseMatchStatus.OFFER_ACCEPTED,
      ECaseMatchStatus.ALREADY_HIRED,
    ]);

    const requiresSpecifiedSkillCertificateFile =
      !exemptSpecifiedSkillCert &&
      isRequiredForStatus([
        ECaseMatchStatus.PROPOSING,
        ECaseMatchStatus.UNDER_SELECTION,
        ECaseMatchStatus.OFFER,
        ECaseMatchStatus.OFFER_ACCEPTED,
        ECaseMatchStatus.ALREADY_HIRED,
      ]);

    // majorId and jobTypeId are required only when status is active (PROPOSING or higher) AND talent has selected specific certificates
    const isActiveStatusForMajorJobType = isRequiredForStatus([
      ECaseMatchStatus.PROPOSING,
      ECaseMatchStatus.UNDER_SELECTION,
      ECaseMatchStatus.OFFER,
      ECaseMatchStatus.OFFER_ACCEPTED,
      ECaseMatchStatus.ALREADY_HIRED,
    ]);
    const requiresMajorId = isActiveStatusForMajorJobType && requiresMajorAndJobType;
    const requiresJobTypeId = isActiveStatusForMajorJobType && requiresMajorAndJobType;

    const requiresCertificateFiles = isRequiredForStatus([
      ECaseMatchStatus.PROPOSING,
      ECaseMatchStatus.UNDER_SELECTION,
      ECaseMatchStatus.OFFER,
      ECaseMatchStatus.OFFER_ACCEPTED,
      ECaseMatchStatus.ALREADY_HIRED,
    ]);

    const requiresCompanyOfferDate = isRequiredForStatus([ECaseMatchStatus.OFFER, ECaseMatchStatus.OFFER_ACCEPTED, ECaseMatchStatus.ALREADY_HIRED]);

    const requiresOfferAcceptDate = isRequiredForStatus([ECaseMatchStatus.OFFER_ACCEPTED, ECaseMatchStatus.ALREADY_HIRED]);

    const requiresJoinDateExpected = isRequiredForStatus([ECaseMatchStatus.OFFER_ACCEPTED, ECaseMatchStatus.ALREADY_HIRED]);

    const requiresSalesFee = isRequiredForStatus([ECaseMatchStatus.OFFER_ACCEPTED, ECaseMatchStatus.ALREADY_HIRED]);

    const requiresPurchaseFee = isRequiredForStatus([ECaseMatchStatus.OFFER_ACCEPTED, ECaseMatchStatus.ALREADY_HIRED]);

    const requiresCompanyJoiningDate = isRequiredForStatus([ECaseMatchStatus.ALREADY_HIRED]);

    return validator.object().shape({
      casePersonInChargeId: validator.number().isSelection().label("案件マッチング担当者").nullable().required(),
      talentPersonInChargeId: validator.number().isSelection().label("人材マッチング担当者").nullable().required(),
      proposalDate: requiresProposalDate
        ? validator.mixed().isDayjs().label("推薦日").nullable().required().test(createMaxDateTest("interviewDate", "面接日"))
        : validator.mixed().isDayjs().label("推薦日").nullable().test(createMaxDateTest("interviewDate", "面接日")),
      interviewDate: requiresInterviewDate
        ? validator
            .mixed()
            .isDayjs()
            .label("面接日")
            .nullable()
            .required()
            .test(createMinDateTest("proposalDate", "推薦日"))
            .test(createMaxDateTest("companyOfferDate", "企業内定日"))
        : validator
            .mixed()
            .isDayjs()
            .label("面接日")
            .nullable()
            .test(createMinDateTest("proposalDate", "推薦日"))
            .test(createMaxDateTest("companyOfferDate", "企業内定日")),
      japaneseLanguageProficiency: requiresJapaneseLanguageProficiency
        ? validator
            .array()
            .label("日本語能力証明書等（実習修了証明含む）")
            .of(validator.number().isSelection().emptyToNull().nullable())
            .test("required-array", JLPT_CERTIFICATE_REQUIRED_MESSAGE, function (value) {
              if (!value) return false;
              if (Array.isArray(value)) {
                const filtered = value.filter((v) => {
                  if (v === null || v === undefined) return false;
                  if (typeof v === "string" && v === "") return false;
                  if (typeof v === "number" && v === -1) return false;
                  return true;
                });
                return filtered.length > 0;
              }
              return false;
            })
            .nullable()
        : validator
            .array()
            .label("日本語能力証明書等（実習修了証明含む）")
            .of(validator.number().isSelection().emptyToNull().nullable())
            .notRequired()
            .nullable(),
      specificSkillCertificates: requiresSpecifiedSkillCertificateFile
        ? validator
            .array()
            .label("特定技能合格証等")
            .of(validator.number().isSelection().emptyToNull().nullable())
            .test("required-array", SPECIFIC_SKILL_CERTIFICATE_REQUIRED_MESSAGE, function (value) {
              if (!value) return false;
              if (Array.isArray(value)) {
                const filtered = value.filter((v) => {
                  if (v === null || v === undefined) return false;
                  if (typeof v === "number" && v === -1) return false;
                  return true;
                });
                return filtered.length > 0;
              }
              return false;
            })
            .nullable()
        : validator.array().label("特定技能合格証等").of(validator.number().isSelection().emptyToNull().nullable()).nullable().notRequired(),
      majorId: requiresMajorId
        ? validator.number().isSelection().label("技能実習　職種").nullable().required()
        : validator.number().isSelection().label("技能実習　職種").nullable().notRequired(),
      jobTypeId: requiresJobTypeId
        ? validator.number().isSelection().label("作業").nullable().required()
        : validator.number().isSelection().label("作業").nullable().notRequired(),
      certificateFiles: validator.mixed().nullable().label("証明書など"),
      // Test owns the data it reads (its own value), avoiding cross-field this.parent.
      // Error is redirected to certificateFiles path so it surfaces beneath the visible FormField.
      certificateFileTypes: requiresCertificateFiles
        ? validator
            .array()
            .of(validator.string().nullable())
            .nullable()
            // Per-file: every uploaded file must have ≥1 document type. Error stays on
            // certificateFileTypes (no path redirect) so it doesn't collide with the
            // certificateFiles-path message from required-types below. UI displays the
            // human message per-file via footerCustom in CaseMatchInfoForm.
            .test("per-file-type-required", "書類の種類を選択してください。", function (value) {
              const files = (this.parent?.certificateFiles ?? []) as unknown[] | null;
              if (!files || files.length === 0) return true;
              for (let i = 0; i < files.length; i++) {
                const csv = (value ?? [])[i] ?? "";
                if (!String(csv).split(",").filter(Boolean).length) return false;
              }
              return true;
            })
            // Cross-file check for missing required document groups. Redirects its error to the
            // `certificateFiles` path. Residence-card 表/裏 duplication is NOT validated here — it is
            // surfaced as a non-blocking orange warning in the UI and must not prevent saving.
            .test("certificate-cross-file-types", "次のファイルが不足しています。", function (value) {
              // Defer until every uploaded file already has at least one type.
              // Without this gate, a freshly uploaded (still-untyped) file would simultaneously
              // surface the per-file 書類の種類 error AND this error, double-noising the UI.
              const files = (this.parent?.certificateFiles ?? []) as unknown[] | null;
              if (files && files.length > 0) {
                const everyFileHasType = files.every((_, i) => String((value ?? [])[i] ?? "").split(",").filter(Boolean).length > 0);
                if (!everyFileHasType) return true;
              }
              const covered = collectCoveredTypeIds(value ?? []);
              const groups = buildRequiredCertificateGroups(talentLocation, exemptSpecifiedSkillCert);
              const missing = groups.filter((g) => !isGroupSatisfied(g, covered));
              if (missing.length > 0) {
                return this.createError({
                  path: "certificateFiles",
                  message: `次のファイルが不足しています。\n${missing.map((g) => `「${g.label}」`).join("、")}`,
                });
              }
              return true;
            })
        : validator
            .array()
            .of(validator.string().nullable())
            .nullable()
            .test("per-file-type-required", "書類の種類を選択してください。", function (value) {
              const files = (this.parent?.certificateFiles ?? []) as unknown[] | null;
              if (!files || files.length === 0) return true;
              for (let i = 0; i < files.length; i++) {
                const csv = (value ?? [])[i] ?? "";
                if (!String(csv).split(",").filter(Boolean).length) return false;
              }
              return true;
            }),
      fitNote: validator.string().trim().label("適合度メモ").max(MAX_TEXT_INPUT_VALIDATION).nullable().notRequired(),
      companyOfferDate: requiresCompanyOfferDate
        ? validator
            .mixed()
            .isDayjs()
            .label("企業内定日")
            .nullable()
            .required()
            .test(createMinDateTest("interviewDate", "面接日"))
            .test(createMaxDateTest("offerAcceptDate", "内定受諾日"))
        : validator
            .mixed()
            .isDayjs()
            .label("企業内定日")
            .nullable()
            .test(createMinDateTest("interviewDate", "面接日"))
            .test(createMaxDateTest("offerAcceptDate", "内定受諾日")),
      offerAcceptDate: requiresOfferAcceptDate
        ? validator
            .mixed()
            .isDayjs()
            .label("内定受諾日")
            .nullable()
            .required()
            .test(createMinDateTest("companyOfferDate", "企業内定日"))
            .test(createMaxDateTest("joinDateExpected", "企業入社日（予定）"))
            .test(createMaxDateTest("companyJoiningDate", "企業入社日"))
        : validator
            .mixed()
            .isDayjs()
            .label("内定受諾日")
            .nullable()
            .test(createMinDateTest("companyOfferDate", "企業内定日"))
            .test(createMaxDateTest("joinDateExpected", "企業入社日（予定）"))
            .test(createMaxDateTest("companyJoiningDate", "企業入社日")),
      joinDateExpected: requiresJoinDateExpected
        ? validator.mixed().isDayjs().label("企業入社日（予定）").nullable().required().test(createMinDateTest("offerAcceptDate", "内定受諾日"))
        : validator.mixed().isDayjs().label("企業入社日（予定）").nullable().test(createMinDateTest("offerAcceptDate", "内定受諾日")),
      salesFee: requiresSalesFee
        ? validator
            .number()
            .commas()
            .emptyToNull()
            .autoTransformToHalfWidth()
            .nullable()
            .integer()
            .min(0)
            .max(MAX_INTEGER)
            .label("紹介料（売上）")
            .required()
        : validator
            .number()
            .commas()
            .emptyToNull()
            .autoTransformToHalfWidth()
            .nullable()
            .integer()
            .min(0)
            .max(MAX_INTEGER)
            .label("紹介料（売上）")
            .notRequired(),
      purchaseFee: requiresPurchaseFee
        ? validator
            .number()
            .commas()
            .emptyToNull()
            .autoTransformToHalfWidth()
            .nullable()
            .integer()
            .min(0)
            .max(MAX_INTEGER)
            .label("紹介料（仕入）")
            .required()
        : validator
            .number()
            .commas()
            .emptyToNull()
            .autoTransformToHalfWidth()
            .nullable()
            .integer()
            .min(0)
            .max(MAX_INTEGER)
            .label("紹介料（仕入）")
            .notRequired(),
      companyJoiningDate: requiresCompanyJoiningDate
        ? validator.mixed().isDayjs().label("企業入社日").nullable().required().test(createMinDateTest("offerAcceptDate", "内定受諾日"))
        : validator.mixed().isDayjs().label("企業入社日").nullable().test(createMinDateTest("offerAcceptDate", "内定受諾日")),
      tagIds: validator.array().of(validator.string()).label("タグ").nullable().notRequired(),
    });
  }, [status, requiresMajorAndJobType, talentLocation, exemptSpecifiedSkillCert]);

  return { caseMatchInfoSchema };
};
